import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import Pusher from 'pusher-js'

const PusherContext = createContext(null)

export function usePusher() {
  return useContext(PusherContext)
}

/**
 * Provides a Pusher connection scoped to the authenticated user.
 * Subscribes to `private-user-{userId}` channel.
 * Falls back gracefully if VITE_PUSHER_KEY is not configured.
 */
export function PusherProvider({ children }) {
  const { isAuthenticated, user, token } = useSelector((state) => state.auth)
  const pusherRef = useRef(null)
  const channelRef = useRef(null)
  const [lastEvent, setLastEvent] = useState(null)

  useEffect(() => {
    const key = import.meta.env.VITE_PUSHER_KEY
    const cluster = import.meta.env.VITE_PUSHER_CLUSTER || 'ap3'

    if (!key || !isAuthenticated || !user?.id) {
      return
    }

    // Initialize Pusher
    const pusher = new Pusher(key, {
      cluster,
      authEndpoint: `${import.meta.env.VITE_API_URL || ''}/api/pusher/auth`,
      auth: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    })

    pusherRef.current = pusher

    // Subscribe to user's private channel
    const channel = pusher.subscribe(`private-user-${user.id}`)

    channel.bind('new-notification', (data) => {
      setLastEvent({ type: 'new-notification', data, timestamp: Date.now() })
    })

    channelRef.current = channel

    return () => {
      channel.unbind_all()
      pusher.unsubscribe(`private-user-${user.id}`)
      pusher.disconnect()
      pusherRef.current = null
      channelRef.current = null
    }
  }, [isAuthenticated, user?.id, token])

  return (
    <PusherContext.Provider value={{ lastEvent, pusher: pusherRef.current }}>
      {children}
    </PusherContext.Provider>
  )
}
